// The design system in code: the Roundstead components from the locked canvas, as React. Styling is app.css on the
// canvas tokens (hc-roundstead.css) and the component bundle.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { BANNER } from '../../content/content.ts';
import { W } from './words.ts';

export const cls = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(' ');

export function Banner() { return <div className="hc-banner" role="status">{BANNER}</div>; }

export function Lockup() {
  return (
    <div className="rs-lockup" aria-label="Roundstead by BOB">
      <svg width="18" height="18" viewBox="0 0 320 320" aria-hidden="true"><path d="M40 272V250H65V70H40V48H166C235 48 272 79 272 130C272 166 252 193 215 204L265 250H288V272H211L145 207H119V250H145V272Z M119 84V173H158C198 173 218 158 218 130C218 100 198 84 158 84Z" fill="currentColor" /></svg>
      <span className="rs-lockup__name">Roundstead</span><span className="rs-lockup__by">by BOB</span>
    </div>
  );
}

export function Segment({ value, onChange }: { value: 'chat' | 'ui'; onChange: (v: 'chat' | 'ui') => void }) {
  return (
    <div className="rs-segment" role="group" aria-label="Show this as">
      {(['chat', 'ui'] as const).map((v) => (
        <button key={v} type="button" className="rs-segment__btn" aria-pressed={value === v} onClick={() => onChange(v)}>{v === 'chat' ? W.chat : W.screens}</button>
      ))}
      <span className="rs-segment__thumb" data-pos={value} aria-hidden="true" />
    </div>
  );
}

type BtnKind = 'primary' | 'secondary' | 'quiet';
export function Button({ kind = 'secondary', children, className, ...rest }: { kind?: BtnKind } & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button type="button" className={cls('hc-btn', `hc-btn--${kind}`, 'rs-press', className)} {...rest}>{children}</button>;
}

export const Chip = ({ children }: { children: ReactNode }) => <span className="hc-chip hc-chip--neutral">{children}</span>;
export const Bracket = ({ children }: { children: ReactNode }) => <span className="hc-bracket">{children}</span>;
export const Eyebrow = ({ children }: { children: ReactNode }) => <span className="rs-eyebrow">{children}</span>;

export const Chevron = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>;

export function Row({ label, value, onClick }: { label: ReactNode; value: ReactNode; onClick?: () => void }) {
  const inner = <><span className="rs-row__label">{label}</span><span className="rs-row__value">{value}{onClick && <Chevron />}</span></>;
  return onClick ? <button type="button" className="rs-row rs-press-soft" onClick={onClick}>{inner}</button> : <div className="rs-row">{inner}</div>;
}

export function KV({ rows, grid }: { rows: [ReactNode, ReactNode][]; grid?: boolean }) {
  return <dl className={cls('rs-kv', grid && 'rs-kv--grid')}>{rows.map(([k, v], i) => <div key={i}><dt>{k}</dt><dd>{v}</dd></div>)}</dl>;
}

/** A sheet that slides up like the phone's own and drags down to close. Focus moves in on open and back on close. */
export function Sheet({ open, title, onClose, children, footer }: { open: boolean; title: string; onClose: () => void; children: ReactNode; footer?: ReactNode }) {
  const [shown, setShown] = useState(open);
  const [drag, setDrag] = useState(0);
  const start = useRef<number | null>(null);
  const panel = useRef<HTMLDivElement>(null);
  const opener = useRef<Element | null>(null);
  useEffect(() => {
    if (open) { opener.current = document.activeElement; setShown(true); document.body.style.overflow = 'hidden'; }
    else { document.body.style.overflow = ''; (opener.current as HTMLElement | null)?.focus?.(); }
  }, [open]);
  // Once the panel is in the page, its title takes focus (the script: opening a sheet moves focus to its title).
  useEffect(() => { if (open && shown) panel.current?.querySelector<HTMLElement>('h2')?.focus({ preventScroll: true }); }, [open, shown]);
  // Escape closes; Tab and Shift-Tab stay inside the open sheet.
  useEffect(() => {
    if (!open) return;
    const k = (e: KeyboardEvent) => {
      if (e.key === 'Escape') return onClose();
      if (e.key !== 'Tab' || !panel.current) return;
      const f = Array.from(panel.current.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')).filter((x) => !x.hasAttribute('disabled'));
      if (!f.length) return;
      const i = f.indexOf(document.activeElement as HTMLElement);
      if (e.shiftKey && i <= 0) { e.preventDefault(); f[f.length - 1].focus(); }
      else if (!e.shiftKey && (i === -1 || i === f.length - 1)) { e.preventDefault(); f[0].focus(); }
    };
    addEventListener('keydown', k); return () => removeEventListener('keydown', k);
  }, [open, onClose]);
  if (!shown) return null;
  const end = () => { if (drag > 110) onClose(); setDrag(0); start.current = null; };
  return (
    <div className={cls('rs-sheet', open ? 'rs-sheet--open' : 'rs-sheet--closing')} onAnimationEnd={() => { if (!open) setShown(false); }}>
      <div className="rs-sheet__scrim" onClick={onClose} aria-hidden="true" />
      <div ref={panel} className="rs-sheet__panel" role="dialog" aria-modal="true" aria-label={title} tabIndex={-1}
           style={drag ? { transform: `translateY(${drag}px)`, transition: 'none' } : undefined}>
        <div className="rs-sheet__grab"
             onPointerDown={(e) => { start.current = e.clientY; (e.target as HTMLElement).setPointerCapture(e.pointerId); }}
             onPointerMove={(e) => { if (start.current !== null) setDrag(Math.max(0, e.clientY - start.current)); }}
             onPointerUp={end} onPointerCancel={end}>
          <div className="hc-panel__handle" aria-hidden="true" />
          <div className="rs-sheet__head"><h2 className="rs-title" tabIndex={-1}>{title}</h2><Button kind="quiet" className="rs-auto" onClick={onClose}>{W.done}</Button></div>
        </div>
        <div className="rs-sheet__body">{children}</div>
        {footer && <div className="rs-sheet__foot">{footer}</div>}
      </div>
    </div>
  );
}

export function Composer({ onSend, disabled, placeholder = W.askQuestion, autoFocus }: { onSend: (t: string) => void; disabled?: boolean; placeholder?: string; autoFocus?: boolean }) {
  const [text, setText] = useState('');
  return (
    <form className="hc-composer rs-composer" onSubmit={(e) => { e.preventDefault(); const t = text.trim(); if (t && !disabled) { setText(''); onSend(t); } }}>
      <input className="hc-composer__input" value={text} onChange={(e) => setText(e.target.value)} placeholder={placeholder} aria-label={placeholder} enterKeyHint="send" autoFocus={autoFocus} />
      <button type="submit" className="rs-send rs-press" aria-label="Send" disabled={disabled || !text.trim()}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 19V5M5 12l7-7 7 7" /></svg>
      </button>
    </form>
  );
}

/** Hidden file input opened by the add-photo buttons. */
export function usePicker(onFiles: (f: FileList) => void) {
  const ref = useRef<HTMLInputElement>(null);
  const input = <input ref={ref} type="file" accept="image/*" multiple hidden onChange={(e) => { if (e.target.files?.length) onFiles(e.target.files); e.target.value = ''; }} />;
  return { open: () => ref.current?.click(), input };
}
